import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  NotificationService,
  ConfigurationService,
  Notification,
  NotificationRecipient,
  ConfigurationState,
  Configuration,
  UserService,
  invalidFormat,
  containsSpecialChars
} from 'remarketing-angular-library';
import { Select, Store } from '@ngxs/store';
import { Observable, combineLatest } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { NgxMaskModule } from 'ngx-mask';
import { SuccessMessageComponent } from '../../../success-message/success-message.component';
import { ErrorMessageComponent } from '../../../error-message/error-message.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { EmailInputComponent, EmailInputConfig } from 'remarketing-ui-components-lib';
import { AlertTrackingConfig, AlertTrackingService } from '../../../../services/alert-tracking/alert-tracking.service';

@Component({
    selector: 'app-alerts-others-add-edit-form',
    templateUrl: './alerts-others-add-edit-form.component.html',
    styleUrls: ['./alerts-others-add-edit-form.component.scss'],
    imports: [TranslocoDirective, NgClass, ErrorMessageComponent, SuccessMessageComponent, FormsModule, ReactiveFormsModule, NgxMaskModule, AsyncPipe, EmailInputComponent]
})
export class AlertsOthersAddEditFormComponent extends SaComponent implements OnInit, AfterViewInit {
  @Select(ConfigurationState.configuration) private configuration$: Observable<Configuration>;
  @Input() tenantName = '';
  @Input() recipientForm: UntypedFormGroup;
  @Input() showCancelWarning: boolean;
  @Output() cancelEdit = new EventEmitter();
  @Output() continueEdit = new EventEmitter();
  @Output() confirmCancel = new EventEmitter();
  @Output() save = new EventEmitter();
  @Output() closeDrawer = new EventEmitter();

  @Input() visibilityFlags: any = [];
  @Input() buyAlerts$: Observable<Array<Notification>>;
  @Input() sellAlerts$: Observable<Array<Notification>>;
  @Input() recipient: NotificationRecipient;
  @ViewChild('formHeading') formHeading: ElementRef<HTMLHeadingElement>;
  optedNotifications: Array<Notification>;
  optedNotificationsOriginal: Array<Notification>;

  hiddenNotifications = ['SSL', 'MOA'];
  user: any;
  showSuccess = false;
  showError = false;
  message: string;
  addMode = false;
  tenantUuid$: Observable<string>;
  tenantUuid = '';
  textSuccessfullyAdded: boolean;
  notifications: Array<Notification>;
  hasSellerEligibility: boolean;

  emailInputConfig = {
    inputFormControlName: 'email',
    labelFor: '"alerts-others-email',
    inputId: 'alerts-others-email'
  } as EmailInputConfig;

  get invalidFormatEmail(): boolean {
    return invalidFormat(this.recipientForm, 'email');
  }
  get emailContainsSpecialChars(): boolean {
    return containsSpecialChars(this.recipientForm, 'email');
  }

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly notificationService: NotificationService,
    private alertTrackingService: AlertTrackingService,
    private store: Store,
    private readonly configurationService: ConfigurationService,
    private readonly _userService: UserService,
    private readonly translocoService: TranslocoService
  ) {
    super();
  }

  get showSellerAlerts() {
    return this._userService.isSellerRole() && this.hasSellerEligibility;
  }

  ngOnInit() {
    this.getTranslations();
    this.optedNotifications = this.recipient ? JSON.parse(JSON.stringify(this.recipient.optedNotifications)) : [];
    this.optedNotificationsOriginal = this.recipient
      ? JSON.parse(JSON.stringify(this.recipient.optedNotifications))
      : [];

    this.addMode = !this.recipient;

    this.store
      .select(storeState => storeState.user)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(data => {
        if (data && data.userProfile) {
          this.user = data.userProfile;
        }
      });
    this.setUserSellerEligibility();

    this.tenantUuid$ = this.configuration$.pipe(map((configuration: Configuration) => configuration.tenantUuid));
    this.tenantUuid$.pipe(takeUntil(this.destroyed$)).subscribe(tenantUuid => (this.tenantUuid = tenantUuid));
    this.notificationService.getNotifications().subscribe(notifications => {
      this.notifications = notifications;
      this.getAlertAndMessageTranslations();
    });
  }

  setUserSellerEligibility() {
    this.store
      .select(state => state.org.organization)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(organization => {
        this.hasSellerEligibility = organization?.organizationProfile.eligibleToSell;
      });
  }

  onSubmit() {
    this.showError = false;
    this.showSuccess = false;

    if (!this.recipientForm.valid) {
      return;
    }

    const payload = {
      ...this.recipient,
      name: this.recipientForm.value.name,
      email: this.recipientForm.value.email,
      role: this.recipientForm.value.role,
      mobilePhone: this.recipientForm.value.phone.replace(/-/g, ''),
      optedNotifications: this.optedNotifications,
      applicationUser: 'N',
      oeUuid: this.user.oeUuid,
      tenantUuid: this.tenantUuid
    };

    if (this.addMode) {
      this.notificationService.createRecipient(payload).subscribe(
        recipient => {
          this.textSuccessfullyAdded = true;
          this.recipient = recipient;
          this.setMessage(this.textSuccessfullyAdded, recipient);
          this.optedNotifications = recipient.optedNotifications;
          this.optedNotificationsOriginal = JSON.parse(JSON.stringify(recipient.optedNotifications));
          this.showSuccess = true;
          this.recipient.mobilePhone = this.reformatPhone(this.recipient.mobilePhone);
          this.trackAlerts(true);
          // focus on buyer name cell
          this.focusElement('name_' + recipient.uuid + '_cell');
          this.recipientForm.reset();
          // update recipient
          this.save.emit(this.recipient);
        },
        () => {
          this.trackAlerts(false);
          this.showError = true;
        }
      );
    } else {
      this.notificationService.updateRecipient(this.recipient.uuid, payload).subscribe(
        recipient => {
          this.textSuccessfullyAdded = false;
          this.recipient = recipient;
          this.setMessage(this.textSuccessfullyAdded, recipient);
          this.optedNotifications = recipient.optedNotifications;
          this.optedNotificationsOriginal = JSON.parse(JSON.stringify(recipient.optedNotifications));
          this.showSuccess = true;
          this.recipient.mobilePhone = this.reformatPhone(this.recipient.mobilePhone);
          this.trackAlerts(true);
          // focus on buyer name cell
          this.focusElement('name_' + recipient.uuid + '_cell');
          (this.recipientForm as any).touched = false;
          // update recipient
          this.save.emit(this.recipient);
          this.recipientForm.markAsPristine();
        },
        () => {
          this.trackAlerts(false);
          this.showError = true;
        }
      );
    }
  }

  checked(type) {
    if (this.optedNotifications) {
      return this.optedNotifications.find(n => n.type === type);
    }
    return false;
  }

  mapAlertTranslations(alerts: Observable<Array<Notification>>) {
    alerts.pipe(takeUntil(this.destroyed$)).subscribe(values => {
      values.forEach(value => {
        value['descriptionCode'] = this.notifications.find(n => n.type === value.type).description;
      });
    });
  }

  getAlertDescription(alert: Notification): string {
    return this.translocoService.translateObject('notifications.' + alert.type);
  }

  checkVisibility(alert) {
    let isVisible = false;
    for (const key in this.visibilityFlags) {
      if (
        alert &&
        alert.descriptionCode &&
        key.toLowerCase() === alert.descriptionCode.toLowerCase() &&
        alert.sendType === 'Email' &&
        !this.hiddenNotifications.includes(alert.type)
      ) {
        isVisible = this.visibilityFlags[key].visible;
      }
    }
    return isVisible;
  }

  private trackAlerts(isSuccess: boolean){
    combineLatest([
      this.buyAlerts$.pipe(take(1)),
      this.sellAlerts$.pipe(take(1))
    ]).subscribe(([buyAlerts,sellAlerts]) =>  {
      const config = this.alertTrackingService.createConfigForBuyersAndOtherRecipients(
        'Other Recipient Alerts',
        buyAlerts,
        sellAlerts,
        this.showSellerAlerts,
        alert => this.checkVisibility(alert),
        alert => this.getAlertDescription(alert),
        type => !!this.checked(type)
      );
      this.alertTrackingService.trackAlertPreferences(config,isSuccess);
    }
    );
  }

  reformatPhone(phone) {
    phone = phone.replace(/-/g, '');
    if (phone.length > 0) {
      if (phone.length === 11) {
        phone = phone.slice(1, 4) + '-' + phone.slice(4, 7) + '-' + phone.slice(7);
        return phone;
      } else {
        phone = phone.slice(0, 3) + '-' + phone.slice(3, 6) + '-' + phone.slice(6);
        return phone;
      }
    }
  }

  updateNotification(e, notification) {
    if (e.target.checked) {
      this.optedNotifications.push(notification);
    } else {
      const i = this.optedNotifications.findIndex(n => n.type === notification.type);
      this.optedNotifications.splice(i, 1);
    }
    if (this.areNotEqual(this.optedNotifications, this.optedNotificationsOriginal)) {
      this.recipientForm.markAsDirty();
    } else {
      this.recipientForm.markAsPristine();
    }
  }

  areNotEqual(array, ogArray?) {
    const arr1 = array.concat().sort((a, b) => a.type.localeCompare(b.type));
    const arr2 = ogArray.concat().sort((a, b) => a.type.localeCompare(b.type));

    if (arr1.length !== arr2.length) return true;

    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i].type !== arr2[i].type) {
        return true;
      }
    }

    return false;
  }

  focusElement(id) {
    setTimeout(() => {
      const element = document.getElementById(id) as HTMLElement;
      if (element) {
        element.focus();
      }
    }, 0);
  }

  close() {
    if (this.areNotEqual(this.optedNotifications, this.optedNotificationsOriginal) || this.recipientForm.dirty) {
      this.showCancelWarning = true;
    } else {
      this.closeDrawer.emit();
    }
  }

  ngAfterViewInit(): void {
    if (this.formHeading && this.formHeading.nativeElement) {
      this.formHeading.nativeElement.focus();
    }
  }

  setMessage(textSuccessfullyAdded, recipient) {
    if (textSuccessfullyAdded) {
      this.translocoService
        .selectTranslate('profileManagement.alertsOthers.textSuccessfullyAdded', { recipient: recipient.name })
        .pipe(take(1))
        .subscribe(translate => {
          this.message = translate;
        });
    } else {
      this.translocoService
        .selectTranslate('profileManagement.alertsOthers.textSuccessfullySetting', { recipient: recipient?.name })
        .pipe(take(1))
        .subscribe(translate => {
          this.message = translate;
        });
    }
  }

  translations = {
    textEmailAddress: '',
    textEnterValidEmail: '',
    emailContainsInvalidChars: ''
  };

  getAlertAndMessageTranslations() {
    this.translocoService
      .selectTranslateObject('')
      .pipe(take(1))
      .subscribe(() => {
        this.mapAlertTranslations(this.buyAlerts$);
        this.mapAlertTranslations(this.sellAlerts$);
        this.setMessage(this.textSuccessfullyAdded, this.recipient);
      });
  }

  getTranslations() {
    const keyParams = {
      'profileManagement.userContact': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translations => {
        this.translations.emailContainsInvalidChars = translations[0].emailContainsInvalidChars;
        this.translations.textEnterValidEmail = translations[0].textEnterValidEmail;
        this.translations.textEmailAddress = translations[0].emailLabel;
      });
  }
}
